'use client';

import React, { useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MastheadHero from './components/MastheadHero';
import EpisodeGrid from './components/EpisodeGrid';
import MarqueeBar from './components/MarqueeBar';
import RundownInterstitial from './components/RundownInterstitial';
import FinalCTA from './components/FinalCTA';
import StickyBanner from './components/StickyBanner';
import { EpisodeCardData } from './components/EpisodeCard';
import { MilestoneData } from './components/MilestoneCard';

/* ──────────────────────────────────────────
   MOCK DATA — Early Era (Episodes 1–49)
   Desaturated / sepia tone
────────────────────────────────────────── */
const earlyEpisodes: EpisodeCardData[] = [
{
  id: 'ep001',
  number: 1,
  title: 'Why We Started Recording in a Closet',
  guest: 'Solo',
  pullQuote: 'The best mic is the one you already own.',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1b3b0f6e3-1772086993867.png",
  thumbnailAlt: 'Vintage microphone on a wooden desk with handwritten notes scattered around',
  duration: '38 min',
  era: 'early',
  tall: true
},
{
  id: 'ep004',
  number: 4,
  title: 'What RSS Actually Does (And Why It Matters)',
  pullQuote: 'RSS is the plumbing nobody talks about until it breaks.',
  thumbnail: "https://images.unsplash.com/photo-1662479696471-b04e2dfec75d",
  thumbnailAlt: 'Close-up of tangled audio cables and connectors on a studio floor',
  duration: '42 min',
  era: 'early'
},
{
  id: 'ep009',
  number: 9,
  title: "The Three-Act Structure You're Already Using",
  guest: 'Nadia Okonkwo',
  pullQuote: 'Every good episode has a turn. Find yours before you hit record.',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1a7b92696-1772086992575.png",
  thumbnailAlt: 'Handwritten story structure notes on index cards pinned to a corkboard',
  duration: '51 min',
  era: 'early',
  tall: true
},
{
  id: 'ep014',
  number: 14,
  title: 'Editing for Honesty, Not Perfection',
  guest: 'Marcus Reyes',
  pullQuote: 'Cut the "ums." Keep the silence. Silence is where trust lives.',
  thumbnail: "https://images.unsplash.com/photo-1616478680608-be144858f990",
  thumbnailAlt: 'Audio waveform on a computer screen in a dimly lit editing suite',
  duration: '44 min',
  era: 'early'
},
{
  id: 'ep019',
  number: 19,
  title: 'How to Book Guests Who Actually Show Up',
  guest: 'Priya Venkataraman',
  pullQuote: 'A confirmation email is not a commitment. A calendar invite is.',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1a5317645-1772086993951.png",
  thumbnailAlt: 'Two people shaking hands across a table with microphones between them',
  duration: '39 min',
  era: 'early'
},
{
  id: 'ep023',
  number: 23,
  title: 'Show Notes Nobody Reads (And How to Fix That)',
  pullQuote: 'Write your show notes for search engines first, humans second.',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1c64ba799-1772086996563.png",
  thumbnailAlt: 'Typewriter with a half-typed page and coffee cup on a worn wooden desk',
  duration: '33 min',
  era: 'early',
  tall: false
},
{
  id: 'ep031',
  number: 31,
  title: 'Mic Technique for Non-Engineers',
  guest: 'Tom Whitfield',
  pullQuote: 'Six inches from the mic. Pop filter on. Breathe through your nose.',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc05b96b-1772086993547.png",
  thumbnailAlt: 'Close-up of a condenser microphone in a home recording setup',
  duration: '47 min',
  era: 'early',
  tall: true
},
{
  id: 'ep038',
  number: 38,
  title: 'When Your Show Isn\'t Growing',
  pullQuote: 'Forty listeners who love you are worth more than four thousand who don\'t care.',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_10ca3d055-1772086997243.png",
  thumbnailAlt: 'Person sitting alone at a radio console looking out a rain-streaked window',
  duration: '55 min',
  era: 'early'
},
{
  id: 'ep044',
  number: 44,
  title: 'The Cold Open That Hooks',
  guest: 'Isabelle Fontaine',
  pullQuote: 'You have thirty seconds before they swipe. Use them like you mean it.',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_18bae5e68-1772086994959.png",
  thumbnailAlt: 'Vintage reel-to-reel tape machine with spools spinning in a broadcast studio',
  duration: '41 min',
  era: 'early'
}];


const milestone1: MilestoneData = {
  id: 'ms-1000',
  label: 'Milestone',
  headline: 'First 1,000 Subscribers',
  body: 'Episode 12. We didn\'t notice until a listener emailed to say they\'d told their entire podcast Slack about us. We recorded a bonus episode in the car on the way home from a coffee shop.',
  year: '2019',
  icon: '🎙️'
};

/* ──────────────────────────────────────────
   MOCK DATA — Mid Era (Episodes 50–99)
   Transitional tone
────────────────────────────────────────── */
const midEpisodes: EpisodeCardData[] = [
{
  id: 'ep051',
  number: 51,
  title: 'Sponsorship Math: What Your Show Is Actually Worth',
  guest: 'Devon Castellano',
  pullQuote: 'CPM is a starting point. Engagement rate is the real number.',
  thumbnail: "https://images.unsplash.com/photo-1725465035060-9570ef762efd",
  thumbnailAlt: 'Calculator and dollar bills on a desk next to a microphone and notepad',
  duration: '58 min',
  era: 'mid',
  tall: true
},
{
  id: 'ep057',
  number: 57,
  title: 'Network vs. Independent: The Real Trade-offs',
  guest: 'Sarah Obi',
  pullQuote: 'A network deal is not a rescue. It\'s a partnership with obligations on both sides.',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1336615e3-1768560521752.png",
  thumbnailAlt: 'Two people in a modern office reviewing documents at a glass-topped conference table',
  duration: '62 min',
  era: 'mid'
},
{
  id: 'ep063',
  number: 63,
  title: 'Dynamic Ad Insertion: Pros, Cons, and the Fine Print',
  pullQuote: 'DAI pays more per impression. It also makes your backcatalog feel like a stranger.',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1ac30bbf9-1771898810525.png",
  thumbnailAlt: 'Server room with blinking lights and cables representing digital infrastructure',
  duration: '49 min',
  era: 'mid'
},
{
  id: 'ep071',
  number: 71,
  title: 'The Episode That Changed Our Format',
  guest: 'Kwame Asante',
  pullQuote: 'We scrapped the opening segment after listener #47 told us she always skipped it.',
  thumbnail: "https://images.unsplash.com/photo-1573742286905-723652f104cc",
  thumbnailAlt: 'Studio control room with mixing board and monitors glowing in the dark',
  duration: '53 min',
  era: 'mid',
  tall: true
},
{
  id: 'ep078',
  number: 78,
  title: 'Interviewing Difficult Guests',
  guest: 'Lena Bergström',
  pullQuote: 'Silence is the most underused interview technique in podcasting.',
  thumbnail: "https://images.unsplash.com/photo-1668605335684-c97ce92cbd76",
  thumbnailAlt: 'Two chairs facing each other in a podcast studio with microphones between them',
  duration: '44 min',
  era: 'mid'
},
{
  id: 'ep085',
  number: 85,
  title: 'Building a Listener Community Without Burning Out',
  pullQuote: 'A Discord server is a full-time job you didn\'t budget for.',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1fdc694ea-1767968059253.png",
  thumbnailAlt: 'Group of people gathered around a table with laptops and notebooks in a community space',
  duration: '46 min',
  era: 'mid'
}];


const milestone2: MilestoneData = {
  id: 'ms-quit',
  label: 'Turning Point',
  headline: 'When We Quit Our Day Jobs',
  body: 'Episode 72. We\'d been doing this nights and weekends for three years. The sponsorship revenue crossed $4,000 a month and we looked at each other across the table and said: "Is this the moment?" It was.',
  year: '2021',
  icon: '✂️'
};

/* ──────────────────────────────────────────
   MOCK DATA — Recent Era (Episodes 100–147)
   Full color
────────────────────────────────────────── */
const recentEpisodes: EpisodeCardData[] = [
{
  id: 'ep101',
  number: 101,
  title: 'The Gimlet Playbook: What Independents Can Steal',
  guest: 'Alex Rivera',
  pullQuote: 'Story structure isn\'t a genre thing. It\'s a respect-for-the-listener thing.',
  thumbnail: "https://images.unsplash.com/photo-1611532736573-418fe4ae3260",
  thumbnailAlt: 'Professional podcast studio with acoustic panels and high-end microphone setup',
  duration: '67 min',
  era: 'recent',
  tall: true
},
{
  id: 'ep108',
  number: 108,
  title: 'Transcript SEO: The Backcatalog Gold Mine',
  pullQuote: 'Every episode you\'ve ever recorded is a landing page waiting to be indexed.',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_1f5a21e41-1772086996571.png",
  thumbnailAlt: 'Laptop open to a search results page in a bright modern workspace with plants',
  duration: '38 min',
  era: 'recent'
},
{
  id: 'ep115',
  number: 115,
  title: 'Narrative Non-Fiction for Audio: A Masterclass',
  guest: 'Yuki Tanaka',
  pullQuote: 'Radio taught us that the best scene is the one you can hear but not see.',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_19a324e2f-1772086992772.png",
  thumbnailAlt: 'Broadcaster at a vintage radio console with headphones and a warm studio light',
  duration: '74 min',
  era: 'recent'
},
{
  id: 'ep122',
  number: 122,
  title: 'Multiplatform Publishing: Audio + Video + Newsletter',
  guest: 'Fatima Al-Rashid',
  pullQuote: 'Your RSS feed is the anchor. Everything else is distribution.',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_19a7b5643-1772086992609.png",
  thumbnailAlt: 'Multiple screens showing podcast, YouTube, and newsletter analytics dashboards',
  duration: '55 min',
  era: 'recent',
  tall: true
},
{
  id: 'ep129',
  number: 129,
  title: 'Sound Design for Non-Sound Designers',
  guest: 'Caleb Oduya',
  pullQuote: 'The right bed track doesn\'t tell the listener what to feel. It gives them permission.',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_139926572-1772086995132.png",
  thumbnailAlt: 'Sound designer working with colorful audio waveforms on dual monitors',
  duration: '61 min',
  era: 'recent'
},
{
  id: 'ep135',
  number: 135,
  title: 'Listener Retention: The 20% Rule',
  pullQuote: 'If 20% of your listeners make it to the end, you\'re doing better than NPR.',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_12edec709-1772086992256.png",
  thumbnailAlt: 'Analytics dashboard showing listener retention curves and drop-off points',
  duration: '43 min',
  era: 'recent'
},
{
  id: 'ep141',
  number: 141,
  title: 'The Wondery Model: Story as IP',
  guest: 'Morgan Steele',
  pullQuote: 'A podcast that becomes a TV show started as a format document, not a microphone.',
  thumbnail: "https://img.rocket.new/generatedImages/rocket_gen_img_18d8fbbec-1772086993976.png",
  thumbnailAlt: 'Clapperboard and headphones on a director\'s chair in a production studio',
  duration: '70 min',
  era: 'recent',
  tall: true
},
{
  id: 'ep147',
  number: 147,
  title: 'On the Craft of Listening',
  guest: 'Jad Abumrad',
  pullQuote: 'Listening is an act of generosity. The host\'s job is to make that generosity easy.',
  thumbnail: "https://images.unsplash.com/photo-1668609045722-da60bc2d4131",
  thumbnailAlt: 'Two people in deep conversation at a podcast table with vintage microphones and coffee',
  duration: '82 min',
  era: 'recent',
  tall: false
}];


const milestone3: MilestoneData = {
  id: 'ms-100',
  label: 'Century Mark',
  headline: 'Episode 100: Live in Front of 200 People',
  body: 'We recorded episode 100 on a stage at a podcasting conference in Chicago. People had flown from three countries. We cried a little. We cut the crying from the final edit. We shouldn\'t have.',
  year: '2022',
  icon: '🎉'
};

/* ──────────────────────────────────────────
   Build grid items
────────────────────────────────────────── */
type GridItem =
{type: 'episode';data: EpisodeCardData;} |
{type: 'milestone';data: MilestoneData;};

const earlyGridItems: GridItem[] = [
...earlyEpisodes.slice(0, 5).map((ep) => ({ type: 'episode' as const, data: ep })),
{ type: 'milestone', data: milestone1 },
...earlyEpisodes.slice(5).map((ep) => ({ type: 'episode' as const, data: ep }))];


const midGridItems: GridItem[] = [
...midEpisodes.slice(0, 3).map((ep) => ({ type: 'episode' as const, data: ep })),
{ type: 'milestone', data: milestone2 },
...midEpisodes.slice(3).map((ep) => ({ type: 'episode' as const, data: ep }))];


const recentGridItems: GridItem[] = [
...recentEpisodes.slice(0, 5).map((ep) => ({ type: 'episode' as const, data: ep })),
{ type: 'milestone', data: milestone3 },
...recentEpisodes.slice(5).map((ep) => ({ type: 'episode' as const, data: ep }))];


/* ──────────────────────────────────────────
   Page Component
────────────────────────────────────────── */
export default function HomePage() {
  /* Global reveal observer for any .reveal elements not in grids */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { rootMargin: '0px 0px -40px 0px', threshold: 0.06 }
    );

    /* Observe non-grid reveal elements */
    document.querySelectorAll('.reveal:not(.masonry-item .reveal)').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Noise overlay */}
      <div className="noise-overlay" aria-hidden="true" />

      {/* Fixed header */}
      <Header />

      {/* Sticky email banner (appears after 2 scroll-depths) */}
      <StickyBanner />

      <main>
        {/* ── 1. Newspaper Masthead Hero ── */}
        <MastheadHero />

        {/* ── Marquee topic bar ── */}
        <MarqueeBar />

        {/* ── 2. Early Archive (Eps 1–49) ── */}
        <EpisodeGrid
          items={earlyGridItems}
          sectionLabel="The Early Years  ·  Episodes 1 – 49  ·  2018 – 2020" />
        

        {/* ── 3. Get the Rundown Interstitial (after Ep 50 milestone) ── */}
        <RundownInterstitial />

        {/* ── 4. Mid Archive (Eps 50–99) ── */}
        <EpisodeGrid
          items={midGridItems}
          sectionLabel="Finding the Voice  ·  Episodes 50 – 99  ·  2020 – 2022" />
        

        {/* ── 5. Recent Archive (Eps 100–147) ── */}
        <EpisodeGrid
          items={recentGridItems}
          sectionLabel="Full Color  ·  Episodes 100 – 147  ·  2022 – Present" />
        

        {/* ── Final CTA ── */}
        <FinalCTA />
      </main>

      {/* Footer */}
      <Footer />
    </>);

}